import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * Cloudflare Turnstile widget.
 *
 * Loads the Cloudflare script once, renders a managed widget, and calls
 * `onVerify(token)` when CF issues a response token. Includes:
 *   - Retry button with widget reset
 *   - 25s watchdog so a stuck "Verifying..." surfaces actionable text
 *   - Per-error-code messages so the user knows whether it's a config issue
 *     (CF dashboard hostname allow list) vs. a transient one
 *
 * Falls back to nothing if `siteKey` isn't provided (dev / preview without CF).
 *
 * Props:
 *  - siteKey:    string (Turnstile site key from CF dashboard)
 *  - onVerify:   (token: string) => void
 *  - onExpire:   () => void  (optional)
 *  - theme:      'light' | 'dark' | 'auto'  (default 'light')
 */
const SCRIPT_ID = 'cf-turnstile-script';
const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let scriptLoading = null;

function loadTurnstileScript() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (scriptLoading) return scriptLoading;
  scriptLoading = new Promise((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      existing.addEventListener('load', () => resolve(window.turnstile));
      existing.addEventListener('error', reject);
      return;
    }
    const s = document.createElement('script');
    s.id = SCRIPT_ID;
    s.src = SCRIPT_URL;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve(window.turnstile);
    s.onerror = reject;
    document.head.appendChild(s);
  });
  return scriptLoading;
}

// Cloudflare error codes — surface actionable messages instead of opaque codes.
// Reference: https://developers.cloudflare.com/turnstile/troubleshooting/client-side-errors/
function describeError(code) {
  if (!code) return 'CAPTCHA failed — please retry.';
  const c = String(code);
  if (c.startsWith('110200')) return 'CAPTCHA configuration error: invalid site key. Contact support.';
  if (c.startsWith('110100')) return 'CAPTCHA configuration error: domain not allowed. The site admin needs to add this domain to Cloudflare Turnstile.';
  if (c.startsWith('110001')) return 'CAPTCHA blocked: this domain is not in the allowed list. Contact support.';
  if (c.startsWith('300') || c.startsWith('600')) return 'CAPTCHA challenge failed — please click Retry.';
  return `CAPTCHA error (${c}) — please click Retry.`;
}

export const TurnstileWidget = ({ siteKey, onVerify, onExpire, theme = 'light' }) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [error, setError] = useState(null);
  const [renderKey, setRenderKey] = useState(0); // bump to force re-render

  const handleRetry = useCallback(() => {
    setError(null);
    if (widgetIdRef.current && window.turnstile) {
      try {
        window.turnstile.reset(widgetIdRef.current);
        return;
      } catch (_) { /* fall through to full re-render */ }
    }
    // Force a clean re-mount of the widget div so Cloudflare gets a fresh start
    setRenderKey((k) => k + 1);
  }, []);

  useEffect(() => {
    if (!siteKey || !containerRef.current) return;
    let cancelled = false;

    loadTurnstileScript()
      .then((turnstile) => {
        if (cancelled || !turnstile || !containerRef.current) return;
        widgetIdRef.current = turnstile.render(containerRef.current, {
          sitekey: siteKey,
          callback: (token) => {
            setError(null);
            onVerify?.(token);
          },
          'expired-callback': () => {
            onExpire?.();
          },
          'error-callback': (code) => {
            // eslint-disable-next-line no-console
            console.error('[Turnstile] error-callback:', code);
            setError(describeError(code));
          },
          'timeout-callback': () => {
            // Cloudflare gave up retrying — surface to the user.
            setError('CAPTCHA timed out — please click Retry.');
          },
          appearance: 'always',
          retry: 'auto',
          'retry-interval': 8000,
          theme,
        });
      })
      .catch(() => setError('Could not load CAPTCHA — check your network.'));

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try { window.turnstile.remove(widgetIdRef.current); } catch (_) { /* noop */ }
        widgetIdRef.current = null;
      }
    };
    // renderKey lets a manual retry tear down + re-render the widget
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteKey, theme, renderKey]);

  if (!siteKey) return null;
  return (
    <div className="space-y-1" data-testid="turnstile-widget">
      <div ref={containerRef} key={renderKey} />
      {error && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-900 space-y-1.5" data-testid="turnstile-error">
          <p className="leading-snug">{error}</p>
          <button
            type="button"
            onClick={handleRetry}
            className="text-amber-900 underline font-medium hover:text-amber-700"
            data-testid="turnstile-retry-btn"
          >
            Retry CAPTCHA
          </button>
        </div>
      )}
    </div>
  );
};

export default TurnstileWidget;
